import {
  type InfiniteData,
  keepPreviousData,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import api from "@/app/api";
import { v2Fetch } from "@/lib/v2-api/client";
import type {
  CompanyMemberUpdate,
  CompanyMembersPage,
  CompanyResponse,
  CompanyUpdate,
  CompanyUserCreate,
  CompanyUserUpdate,
} from "@/app/api/types/company";
import type { UserResponse } from "@/app/api/types/user";
import type { UUID } from "node:crypto";
import { invalidateMemberGraph } from "@/query/invalidate-member-graph";

function updateMemberInPages(
  data: InfiniteData<CompanyMembersPage>,
  userId: UUID,
  patch: Partial<UserResponse>,
): InfiniteData<CompanyMembersPage> {
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((m) => (m.id === userId ? { ...m, ...patch } : m)),
    })),
  };
}

function removeMemberFromPages(
  data: InfiniteData<CompanyMembersPage>,
  userId: UUID,
): InfiniteData<CompanyMembersPage> {
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.filter((m) => m.id !== userId),
      total: page.total - 1,
    })),
  };
}

// Query keys
export const companyKeys = {
  all: ["company"] as const,
  me: () => [...companyKeys.all, "me"] as const,
  // Prefix used for invalidation (matches every per-company members query).
  members: () => [...companyKeys.all, "members"] as const,
  // Members are scoped by company id so a persisted snapshot can never be
  // restored across a tenant switch (multi-tenant isolation — Constitution
  // Art. III §3.1). Logout also clears the cache; this is defence in depth.
  membersByCompany: (companyId: string) =>
    [...companyKeys.members(), companyId] as const,
  // Nested under membersByCompany so member mutations (which invalidate the
  // `members()` prefix) also refresh the paginated search + count.
  membersSearch: (companyId: string, search: string, role: string) =>
    [
      ...companyKeys.membersByCompany(companyId),
      "search",
      search,
      role,
    ] as const,
} as const;

// Server pagination page size for the company Users tab.
export const COMPANY_MEMBERS_PAGE_SIZE = 25;

// Get current company
export function useCompany(options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: companyKeys.me(),
    queryFn: () => api.companies.getMe(),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: options?.enabled ?? true,
  });
}

// Update company
export function useUpdateCompany() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CompanyUpdate) => api.companies.updateMe(data),
    onMutate: async (newData: CompanyUpdate) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({ queryKey: companyKeys.me() });

      // Snapshot previous value
      const previousCompany = queryClient.getQueryData<CompanyResponse>(
        companyKeys.me(),
      );

      // Optimistically update to the new value
      if (previousCompany) {
        queryClient.setQueryData<CompanyResponse>(companyKeys.me(), (old) => {
          if (!old) return old;
          // Only update fields that are defined (not Undefined)
          const updates: Partial<CompanyResponse> = {};
          Object.keys(newData).forEach((key) => {
            const value = (newData as any)[key];
            if (value !== undefined && typeof value !== "symbol") {
              (updates as any)[key] = value;
            }
          });
          return { ...old, ...updates };
        });
      }

      // Return context with snapshot
      return { previousCompany };
    },
    onError: (err, newData, context) => {
      // Rollback on error
      if (context?.previousCompany) {
        queryClient.setQueryData(companyKeys.me(), context.previousCompany);
      }
    },
    onSuccess: (updatedCompany: CompanyResponse) => {
      // Update cache with server response
      queryClient.setQueryData(companyKeys.me(), updatedCompany);
    },
    onSettled: () => {
      // Refetch to ensure we're in sync with server
      queryClient.invalidateQueries({ queryKey: companyKeys.me() });
    },
  });
}

// Update company BI context — persistent, company-wide, BI-mode-only
// instructions for the BI agent. Mirrors useUpdateUserBIContext but at company
// scope: the value is stored on the company (under company_metadata) and
// injected into BI-mode chat prompts before the per-user block. The write goes
// through the V2 API (`PATCH /api/v2/bi-context/company`, self-company via the
// JWT); the read comes from the company object (CompanyResponse.bi_context via
// GET /companies/me).
export function useUpdateCompanyBIContext() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (bi_context: string | null) =>
      v2Fetch<{ biContext: string | null }>("/api/v2/bi-context/company", {
        method: "PATCH",
        body: JSON.stringify({ biContext: bi_context }),
      }),
    onMutate: async (newBIContext: string | null) => {
      await queryClient.cancelQueries({ queryKey: companyKeys.me() });
      const previousCompany = queryClient.getQueryData<CompanyResponse>(
        companyKeys.me(),
      );
      if (previousCompany) {
        queryClient.setQueryData<CompanyResponse>(companyKeys.me(), (old) =>
          old ? { ...old, bi_context: newBIContext } : old,
        );
      }
      return { previousCompany };
    },
    onError: (_err, _newBIContext, context) => {
      if (context?.previousCompany) {
        queryClient.setQueryData(companyKeys.me(), context.previousCompany);
      }
    },
    onSuccess: (result: { biContext: string | null }) => {
      queryClient.setQueryData<CompanyResponse>(companyKeys.me(), (old) =>
        old ? { ...old, bi_context: result.biContext } : old,
      );
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: companyKeys.me() });
    },
  });
}

// List the members (user accounts) of the current company's tenant. The cache
// is keyed by companyId so a persisted snapshot is never reused across tenants;
// the query stays disabled until the caller knows which company it belongs to.
export function useCompanyMembers(
  companyId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  return useQuery<UserResponse[]>({
    queryKey: companyKeys.membersByCompany(companyId ?? "none"),
    queryFn: () => api.companies.getMembers(),
    enabled: !!companyId && (options?.enabled ?? true),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

// Server-paginated + searchable roster for the Users tab. Deliberately separate
// from useCompanyMembers (the full list still backs the tab count, EmployeesTab's
// linked-user resolution, and the link-employee picker), so those never regress
// while the Users tab stops shipping every account to the client at once.
export function useCompanyMembersSearch(
  companyId: string | null | undefined,
  { search, role }: { search: string; role: "all" | "owner" | "member" },
) {
  return useInfiniteQuery({
    queryKey: companyKeys.membersSearch(companyId ?? "none", search, role),
    queryFn: ({ pageParam }) =>
      api.companies.searchMembers({
        search: search || undefined,
        role: role === "all" ? undefined : role,
        limit: COMPANY_MEMBERS_PAGE_SIZE,
        offset: pageParam,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      // A zero-row page (e.g. rows deleted concurrently below the loaded offset)
      // would leave the offset unchanged — stop paginating instead of hammering
      // the same empty page forever.
      if (lastPage.items.length === 0) return undefined;
      const loaded = allPages.reduce((n, p) => n + p.items.length, 0);
      return loaded < lastPage.total ? loaded : undefined;
    },
    // Keep the current page rendered while the next search/filter result loads,
    // so the roster doesn't blank → skeleton on every keystroke / role toggle.
    placeholderData: keepPreviousData,
    enabled: !!companyId,
    staleTime: 5 * 60 * 1000,
  });
}

// Update a member's role (owner only)
export function useUpdateMemberRole() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      userId,
      data,
    }: {
      userId: UUID;
      data: CompanyMemberUpdate;
    }) => api.companies.members.putUserId(userId, data),
    onMutate: async ({ userId, data }) => {
      await queryClient.cancelQueries({ queryKey: companyKeys.members() });

      // Snapshot all queries under the members prefix for rollback
      const previousQueries = queryClient.getQueriesData({
        queryKey: companyKeys.members(),
      });

      // Optimistically update paginated (infinite) queries
      queryClient.setQueriesData<InfiniteData<CompanyMembersPage>>(
        { queryKey: companyKeys.members() },
        (old) => {
          if (!old || !("pages" in old)) return old;
          return updateMemberInPages(old, userId, {
            company_permissions: data.role,
          });
        },
      );

      // Also update flat member lists (useCompanyMembers)
      queryClient.setQueriesData<UserResponse[]>(
        { queryKey: companyKeys.members() },
        (old) => {
          if (!old || !Array.isArray(old)) return old;
          return old.map((member) =>
            member.id === userId
              ? { ...member, company_permissions: data.role }
              : member,
          );
        },
      );

      return { previousQueries };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          if (data) queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      invalidateMemberGraph(queryClient);
    },
  });
}

// Remove a member from the company (owner only). Self-removal is blocked, so
// the endpoint returns only a confirmation message.
export function useRemoveMember() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (userId: UUID) => api.companies.members.deleteUserId(userId),
    onMutate: async (userId) => {
      await queryClient.cancelQueries({ queryKey: companyKeys.members() });

      // Snapshot all queries under the members prefix for rollback
      const previousQueries = queryClient.getQueriesData({
        queryKey: companyKeys.members(),
      });

      // Optimistically remove the member from paginated (infinite) queries
      queryClient.setQueriesData<InfiniteData<CompanyMembersPage>>(
        { queryKey: companyKeys.members() },
        (old) => {
          if (!old || !("pages" in old)) return old;
          return removeMemberFromPages(old, userId);
        },
      );

      // Also update flat member lists (useCompanyMembers)
      queryClient.setQueriesData<UserResponse[]>(
        { queryKey: companyKeys.members() },
        (old) => {
          if (!old || !Array.isArray(old)) return old;
          return old.filter((member) => member.id !== userId);
        },
      );

      return { previousQueries };
    },
    onError: (_err, _vars, context) => {
      if (context?.previousQueries) {
        for (const [key, data] of context.previousQueries) {
          if (data) queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: () => {
      invalidateMemberGraph(queryClient);
    },
  });
}

// ---- Owner-only user administration (Company Management dashboard) ----

// Create a brand-new login account inside the company (owner only).
export function useCreateCompanyUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CompanyUserCreate) => api.companies.createUser(data),
    onSuccess: () => {
      // A new account joins the members list and the agency add-user picker.
      invalidateMemberGraph(queryClient);
    },
  });
}

// Edit a member's identity — name / email / phone (owner only).
export function useUpdateCompanyUser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ userId, data }: { userId: UUID; data: CompanyUserUpdate }) =>
      api.companies.updateUser(userId, data),
    onSuccess: () => {
      // A user's name/email surfaces across the member graph (members list,
      // group rows, employee "linked to" labels); also refresh the current-user
      // cache, since an owner can edit their own identity.
      invalidateMemberGraph(queryClient);
      queryClient.invalidateQueries({ queryKey: ["user"] });
    },
  });
}

// Reset a member's password (owner only). With no password the server returns
// a generated temporary one in `temporary_password`.
export function useResetUserPassword() {
  return useMutation({
    mutationFn: ({
      userId,
      password,
    }: {
      userId: UUID;
      password?: string | null;
    }) => api.companies.resetUserPassword(userId, { password }),
  });
}

// Email a member a self-service password-reset link (owner only).
export function useSendUserRecovery() {
  return useMutation({
    mutationFn: (userId: UUID) => api.companies.sendUserRecovery(userId),
  });
}
